/**
 * @fileoverview Image upload utilities.
 *
 * Provides client-side validation, metadata extraction, and AVIF/HEIC→JPEG/PNG
 * normalization for image uploads before sending to the backend. Provider-facing
 * storage only ever sees JPEG/PNG/WebP — AVIF is decoded by the browser and
 * re-encoded via canvas, HEIC is decoded by a lazy-loaded wasm decoder, both at
 * the upload entry points.
 */

import { FileReadError } from '@/lib/fileReadError'
import { captureSentryError } from '@/lib/sentry'
import { loadHeicDecoder } from './heicDecoder'

/**
 * Maximum allowed image file size (50 MB).
 * Matches the backend MAX_FRAME_FETCH_SIZE so images accepted here can always
 * be fetched at generation time.
 */
export const MAX_IMAGE_FILE_SIZE = 50 * 1024 * 1024

/**
 * Allowed image MIME types accepted by drop/paste/file-picker entry points.
 * AVIF and HEIC are accepted as input but normalized to JPEG/PNG before
 * upload — see `normalizeImageFile` / `normalizeImageDataUrl` below.
 */
export const ALLOWED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/heic',
  'image/heif',
] as const

/**
 * Allowed image file extensions.
 */
export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.avif', '.heic', '.heif'] as const

/**
 * Canonical `accept` attribute value for image file pickers. Derived from the
 * allowlists so a new input format lands in every picker with one edit here.
 *
 * Do NOT migrate mobile camera-roll inputs (CameraRollUpload's
 * CAMERA_ROLL_ACCEPT) to this: offering `image/heic` in an accept string makes
 * iOS Safari hand over the original HEIC instead of transcoding camera-roll
 * picks to JPEG for free — the wasm decode is the slow fallback, not the goal.
 */
export const IMAGE_ACCEPT = [...ALLOWED_IMAGE_MIME_TYPES, ...ALLOWED_IMAGE_EXTENSIONS].join(',')

type AllowedImageMime = (typeof ALLOWED_IMAGE_MIME_TYPES)[number]
type AllowedImageExtension = (typeof ALLOWED_IMAGE_EXTENSIONS)[number]

/**
 * Validation result for an image file.
 */
export interface ImageValidationResult {
  valid: boolean
  error?: string
}

/**
 * Extracted image metadata.
 */
export interface ExtractedImageMetadata {
  width: number
  height: number
}

function getExtension(filename: string): string {
  const idx = filename.lastIndexOf('.')
  if (idx < 0) return ''
  return filename.slice(idx).toLowerCase()
}

function isAllowedMime(type: string): boolean {
  return ALLOWED_IMAGE_MIME_TYPES.includes(type as AllowedImageMime)
}

function isAllowedExtension(ext: string): boolean {
  return ALLOWED_IMAGE_EXTENSIONS.includes(ext as AllowedImageExtension)
}

/**
 * Validates an image file for upload.
 * Checks file extension and MIME type.
 *
 * @param file - The file to validate
 * @returns Validation result with error message if invalid
 */
export function validateImageFile(file: File): ImageValidationResult {
  if (file.size > MAX_IMAGE_FILE_SIZE) {
    return {
      valid: false,
      error: 'Image exceeds 50 MB. Use a smaller file or export as JPEG.',
    }
  }

  const extension = getExtension(file.name)
  if (!isAllowedExtension(extension)) {
    return {
      valid: false,
      error: `Invalid image format. Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')}`,
    }
  }

  // Empty MIME types are common for Finder drags / older OSes, so warning every
  // time would just be noise. We only flag a *non-empty* MIME that disagrees
  // with the extension since that's the surprising case worth investigating.
  if (file.type !== '' && !isAllowedMime(file.type)) {
    console.warn(`[Image] MIME type ${file.type} not in allowlist, but extension ${extension} is valid`)
  }

  return { valid: true }
}

/**
 * Look up the canonical image MIME type for a file extension. Used to repair
 * empty `file.type` values from Finder drags so downstream `FileReader` data
 * URLs declare the right media type. Returns null for unknown extensions.
 */
function imageMimeFromExtension(extension: string): AllowedImageMime | null {
  switch (extension.toLowerCase()) {
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg'
    case '.png':
      return 'image/png'
    case '.webp':
      return 'image/webp'
    case '.avif':
      return 'image/avif'
    case '.heic':
      return 'image/heic'
    case '.heif':
      return 'image/heif'
    default:
      return null
  }
}

/** Timeout for image metadata extraction (30 seconds) */
const IMAGE_LOAD_TIMEOUT_MS = 30000

/**
 * Extracts metadata from an image file using HTML5 Image element.
 *
 * @param file - The image file to extract metadata from
 * @returns Promise resolving to extracted metadata (width, height)
 * @throws Error if image cannot be loaded or is corrupt
 */
export function extractImageMetadata(file: File): Promise<ExtractedImageMetadata> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl)
    }

    const timeout = setTimeout(() => {
      cleanup()
      reject(new Error('Image loading timed out. The file may be too large or corrupt.'))
    }, IMAGE_LOAD_TIMEOUT_MS)

    img.onload = () => {
      clearTimeout(timeout)
      const metadata: ExtractedImageMetadata = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      }
      cleanup()
      resolve(metadata)
    }

    img.onerror = () => {
      clearTimeout(timeout)
      cleanup()
      reject(new Error('Failed to load image file. The file may be corrupt or in an unsupported format.'))
    }

    img.src = objectUrl
  })
}

/**
 * Canonical pre-normalization MIME for analytics (`upload_started`'s
 * `original_mime_type`). Extension wins for the formats we convert — an
 * empty-MIME or OS-mislabeled `.heic` is still a HEIC, and reporting the
 * browser's `type` there would undercount conversions. Falls back to the
 * browser-reported type, then extension inference; undefined when unknown.
 */
export function originalImageMime(file: File): string | undefined {
  const ext = getExtension(file.name)
  if (ext === '.heic' || ext === '.heif' || ext === '.avif') {
    return imageMimeFromExtension(ext) ?? undefined
  }
  return file.type || (imageMimeFromExtension(ext) ?? undefined)
}

/**
 * Checks if a file is an image file based on MIME type, falling back to
 * extension. Some Finder drags and odd browsers/OSes deliver Files with
 * an empty `type` — without the extension fallback those would never be
 * routed to the image upload pipeline.
 */
export function isImageFile(file: File): boolean {
  if (isAllowedMime(file.type)) return true
  return isAllowedExtension(getExtension(file.name))
}

/**
 * Checks if a DataTransfer contains image files.
 */
export function hasImageFiles(dataTransfer: DataTransfer): boolean {
  if (!dataTransfer.types.includes('Files')) return false
  for (let i = 0; i < dataTransfer.items.length; i++) {
    const item = dataTransfer.items[i]
    if (item.kind !== 'file') continue
    if (isAllowedMime(item.type)) return true
    // DataTransferItem doesn't expose filename pre-drop, so MIME is the only
    // signal here. An empty type (some Finder/AirDrop drags) must count as a
    // candidate — rejecting it would suppress dragover's preventDefault and
    // the drop would never happen for files the drop handlers themselves
    // would accept by extension. The real File is re-checked at drop time.
    if (item.type === '') return true
  }
  return false
}

/**
 * Gets the first image file from a DataTransfer.
 */
export function getImageFileFromDataTransfer(dataTransfer: DataTransfer): File | null {
  if (dataTransfer.files.length === 0) return null
  for (let i = 0; i < dataTransfer.files.length; i++) {
    const file = dataTransfer.files[i]
    if (isImageFile(file)) return file
  }
  return null
}

// ---------------------------------------------------------------------------
// AVIF normalization
//
// Browsers (Chrome/Edge 85+, Firefox 113+, Safari 16.4+) decode AVIF natively
// via <img>, so we paint to a canvas and re-encode to JPEG (or PNG when alpha
// is present). The result flows through the existing pipeline as if the user
// had picked a JPEG/PNG to begin with — downstream R2 and AI providers never
// see AVIF.
// ---------------------------------------------------------------------------

const AVIF_MIME = 'image/avif'
const AVIF_EXTENSION = '.avif'
const NORMALIZED_JPEG_QUALITY = 0.92

function isAvifFile(file: File): boolean {
  if (file.type === AVIF_MIME) return true
  return getExtension(file.name) === AVIF_EXTENSION
}

// Matches image/heic, image/heif, and their -sequence variants.
const HEIC_MIME_PATTERN = /^image\/hei[cf]/i

function isHeicFile(file: File): boolean {
  if (HEIC_MIME_PATTERN.test(file.type)) return true
  const ext = getExtension(file.name)
  return ext === '.heic' || ext === '.heif'
}

/**
 * Decode a HEIC/HEIF File and re-encode as JPEG. Chrome/Firefox can't decode
 * HEIC in `<img>` (HEVC patents), so unlike AVIF this goes through a wasm
 * decoder — lazy-loaded so only users who touch a HEIC file pay for the chunk.
 */
async function convertHeicFile(file: File): Promise<File> {
  let heic: Awaited<ReturnType<typeof loadHeicDecoder>>
  try {
    heic = await loadHeicDecoder()
  } catch (importError) {
    captureSentryError(importError, {
      operation: 'heic_decoder_import_failed',
      component: 'uploadImage',
    })
    throw importError
  }
  // ponytail: always JPEG — camera HEICs are opaque; add an alpha-detect PNG
  // branch (as in the AVIF path) if alpha-carrying HEIC ever shows up.
  const blob = assertConvertedBlobSize(
    await heic.heicTo({ blob: file, type: 'image/jpeg', quality: NORMALIZED_JPEG_QUALITY }),
  )
  const stripped = file.name.replace(/\.(heic|heif)$/i, '')
  const baseName = stripped !== '' ? stripped : 'image'
  const newName = baseName.endsWith('.jpg') ? baseName : `${baseName}.jpg`
  return new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified })
}

function isAvifDataUrl(dataUrl: string): boolean {
  return dataUrl.toLowerCase().startsWith('data:image/avif')
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const timeout = setTimeout(() => {
      reject(new Error('AVIF decode timed out. The file may be too large or corrupt.'))
    }, IMAGE_LOAD_TIMEOUT_MS)
    img.onload = () => {
      clearTimeout(timeout)
      resolve(img)
    }
    img.onerror = () => {
      clearTimeout(timeout)
      reject(new Error('Failed to decode AVIF image. Your browser may not support AVIF.'))
    }
    img.src = src
  })
}

/** Sampled rows for alpha detection; bounds full-frame ImageData allocation. */
const ALPHA_SAMPLE_ROWS = 64

/**
 * Detects whether the canvas content has any non-opaque pixels.
 *
 * Reads up to ALPHA_SAMPLE_ROWS rows from the top of the canvas instead of the
 * full image — a 12 MP source would otherwise allocate ~48 MB of RGBA buffer
 * just to scan for alpha, which can OOM constrained tabs on mobile.
 *
 * The trade-off: a fully-opaque-on-top, alpha-on-bottom image (rare) would be
 * misclassified as opaque and round-trip to JPEG, losing the alpha. We accept
 * that for the memory ceiling.
 */
function detectAlpha(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
  const sampleHeight = Math.min(height, ALPHA_SAMPLE_ROWS)
  const data = ctx.getImageData(0, 0, width, sampleHeight).data
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 255) return true
  }
  return false
}

/**
 * Converted output must honor the same ceiling as accepted input — the limit
 * mirrors the backend's fetch cap, so it binds the bytes we upload, not the
 * file the user picked. HEIC/AVIF compress tighter than JPEG/PNG, so a
 * within-limit source can re-encode past it.
 */
function assertConvertedBlobSize(blob: Blob): Blob {
  if (blob.size > MAX_IMAGE_FILE_SIZE) {
    throw new Error('Image exceeds 50 MB after conversion. Use a smaller file or export as JPEG.')
  }
  return blob
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: 'image/jpeg' | 'image/png',
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error(`Canvas re-encode to ${mimeType} returned no blob.`))
          return
        }
        resolve(blob)
      },
      mimeType,
      quality,
    )
  })
}

export function canvasToDataUrl(
  canvas: HTMLCanvasElement,
  mimeType: 'image/jpeg' | 'image/png',
  quality?: number,
): string {
  return canvas.toDataURL(mimeType, quality)
}

function paintToCanvas(img: HTMLImageElement): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Failed to create canvas 2d context for AVIF normalization')
  ctx.drawImage(img, 0, 0)
  return { canvas, ctx }
}

/**
 * Normalize a File for upload. AVIF (by MIME or extension) is decoded via
 * <img>, painted to canvas, and re-encoded as JPEG (or PNG when alpha is
 * present); HEIC/HEIF is decoded via the wasm decoder and re-encoded as JPEG.
 * The returned File has a matching MIME type and an updated `.jpg`/`.png`
 * extension; the basename is preserved.
 *
 * Other inputs are returned unchanged.
 */
export async function normalizeImageFile(file: File): Promise<File> {
  const needsConversion = isHeicFile(file) || isAvifFile(file)
  if (!needsConversion) return file

  // Enforce the input ceiling before decoding either format — some callers
  // (e.g. readImageFileAsDataUrl consumers) normalize before validating, and
  // decoding an oversized source burns seconds and memory on a file that
  // gets rejected anyway. Passthrough files are untouched: rejecting them is
  // validateImageFile's job, with its friendlier error surface.
  if (file.size > MAX_IMAGE_FILE_SIZE) {
    throw new Error('Image exceeds 50 MB. Use a smaller file or export as JPEG.')
  }

  if (isHeicFile(file)) return convertHeicFile(file)

  const objectUrl = URL.createObjectURL(file)
  try {
    const img = await loadHtmlImage(objectUrl)
    const { canvas, ctx } = paintToCanvas(img)
    const hasAlpha = detectAlpha(ctx, canvas.width, canvas.height)
    const targetMime: 'image/jpeg' | 'image/png' = hasAlpha ? 'image/png' : 'image/jpeg'
    const targetExtension = hasAlpha ? '.png' : '.jpg'
    const blob = assertConvertedBlobSize(
      await canvasToBlob(canvas, targetMime, hasAlpha ? undefined : NORMALIZED_JPEG_QUALITY),
    )
    // Strip the .avif suffix; if the original was just `.avif` (no basename),
    // fall back to a default so we don't end up with `.avif.jpg`.
    const stripped = file.name.replace(/\.avif$/i, '')
    const baseName = stripped !== '' ? stripped : 'image'
    const newName = baseName.endsWith(targetExtension) ? baseName : `${baseName}${targetExtension}`
    return new File([blob], newName, { type: targetMime, lastModified: file.lastModified })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/**
 * Read an image File as a data URL after applying file-level normalization.
 * This preserves AVIF support when the browser reports an empty MIME type:
 * once FileReader serializes that File, the `.avif` filename is no longer
 * available to `normalizeImageDataUrl`.
 *
 * Also coerces empty `file.type` from extension-only Finder drags so the
 * resulting data URL declares an allowed media type — otherwise the backend's
 * `validateFrameImageDataUrl` would reject the upload as `image/unknown`.
 */
export async function readImageFileAsDataUrl(file: File): Promise<string> {
  const normalizedFile = await normalizeImageFile(file)
  const fileForReader =
    normalizedFile.type === ''
      ? (() => {
          const inferredMime = imageMimeFromExtension(getExtension(normalizedFile.name))
          return inferredMime
            ? new File([normalizedFile], normalizedFile.name, {
                type: inferredMime,
                lastModified: normalizedFile.lastModified,
              })
            : normalizedFile
        })()
      : normalizedFile
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    // Not `reader.onerror = reject`: the DOM passes a ProgressEvent, which
    // Sentry can only report as an untitled "captured as exception" issue.
    // Reported against the original `file`, not `fileForReader`: the latter may
    // be a normalized/MIME-coerced copy, and the diagnosis needs the file the
    // user actually picked.
    reader.onerror = () => reject(new FileReadError(file, reader.error))
    reader.readAsDataURL(fileForReader)
  })
}

/**
 * Normalize a base64 image data URL. AVIF data URLs are decoded and re-encoded
 * as `data:image/jpeg` or `data:image/png`. Non-AVIF inputs are returned
 * unchanged.
 */
export async function normalizeImageDataUrl(dataUrl: string): Promise<string> {
  if (!isAvifDataUrl(dataUrl)) return dataUrl

  const img = await loadHtmlImage(dataUrl)
  const { canvas, ctx } = paintToCanvas(img)
  const hasAlpha = detectAlpha(ctx, canvas.width, canvas.height)
  const targetMime: 'image/jpeg' | 'image/png' = hasAlpha ? 'image/png' : 'image/jpeg'
  return canvasToDataUrl(canvas, targetMime, hasAlpha ? undefined : NORMALIZED_JPEG_QUALITY)
}
